import { FC } from 'react';
import { getBezierPath, EdgeLabelRenderer, BaseEdge } from 'reactflow';
import type { EdgeProps } from 'reactflow';

export interface ConditionalEdgeData {
  condition?: string;
  condition_type?: 'success' | 'failure' | 'timeout' | 'true' | 'false' | 'custom';
  description?: string;
  label?: string;
}

const ConditionalEdge: FC<EdgeProps<ConditionalEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data = {},
  selected,
  markerEnd,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Tesla color palette
  const edgeColor = {
    success: '#10B981',    // status-online
    failure: '#EF4444',    // accent-red
    timeout: '#F59E0B',    // accent-yellow
    true: '#10B981',       // status-online
    false: '#EF4444',      // accent-red
    custom: '#6B7280',     // text-secondary
  }[data.condition_type || 'custom'] || '#6B7280';

  const edgeLabel = data.label || {
    success: '✓',
    failure: '✗',
    timeout: '⏱',
    true: 'True',
    false: 'False',
    custom: data.condition || '?',
  }[data.condition_type || 'custom'] || '?';

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: edgeColor,
          strokeWidth: selected ? 4 : 3,
          strokeDasharray: data.condition_type === 'custom' ? '5,5' : '0',
          opacity: selected ? 1 : 1,
        }}
      />

      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          {/* Label Badge */}
          <div
            className="
              flex items-center justify-center 
              px-2 py-1 
              text-xs font-semibold text-white 
              rounded-full shadow-sm
              transition-all duration-fast
              hover:scale-110
            "
            style={{ backgroundColor: edgeColor }}
          >
            {edgeLabel}
          </div>

          {/* Hover Tooltip */}
          {data.description && (
            <div
              className="
                absolute top-7 left-1/2 -translate-x-1/2
                bg-popover
                text-foreground
                text-xs px-3 py-2 
                rounded-lg shadow-lg
                border border-border dark:border-border-subtle
                whitespace-nowrap
                opacity-0 hover:opacity-100
                transition-opacity duration-fast
                pointer-events-none
                z-50
              "
            >
              {data.description}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default ConditionalEdge;

